import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Building, Scale, Crown, ChevronRight, Mail,
  CheckCircle, Clock, AlertTriangle, Activity, BarChart3,
  Send, RefreshCw, Eye, ArrowLeft, Settings, Link2, Link2Off,
  Inbox, X, HelpCircle
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import apiService from '../../../services/api';
import '../../../styles/insights.css';
import HowItWorksGuide from '../../../shared/components/HowItWorksGuide';
import GuidedTour from '../../../shared/components/GuidedTour';
import { coordinatorTourSteps } from './coordinatorTourSteps';
import CompanyIdentityReconciliationModal from '../components/CompanyIdentityReconciliationModal';
import PersonaSetup from '../../../components/PersonaSetup';

const CoordinatorDashboard = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  // The assessment whose persona contacts are being collected, or null.
  const [personaSetupFor, setPersonaSetupFor] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null); // { tone, text }
  const [showNewAssessmentForm, setShowNewAssessmentForm] = useState(false);
  const [newAssessmentData, setNewAssessmentData] = useState({
    company_name: '',
    industry_vertical: ''
  });

  const personas = [
    {
      id: 'c_level_tech',
      code: 'c_level_tech',
      name: 'C-Level Technology',
      description: 'CTO, CIO, Chief Technology Officer',
      icon: <Building aria-hidden="true" className="w-6 h-6" />,
      color: 'blue'
    },
    {
      id: 'c_level_hr', 
      code: 'c_level_hr',
      name: 'Human Resources',
      description: 'HR Director, People Operations',
      icon: <Users aria-hidden="true" className="w-6 h-6" />,
      color: 'green'
    },
    {
      id: 'legal',
      code: 'legal', 
      name: 'Legal & Compliance',
      description: 'General Counsel, Compliance Officer',
      icon: <Scale className="w-6 h-6" />,
      color: 'purple'
    },
    {
      id: 'ceo_board',
      code: 'ceo_board',
      name: 'CEO/Board Level', 
      description: 'CEO, Board Members, Executive Leadership',
      icon: <Crown className="w-6 h-6" />,
      color: 'gold'
    }
  ];

  // Tile 15 — inbound PM requests inbox
  const [inboxRequests, setInboxRequests] = useState([]);
  const [inboxBadge, setInboxBadge] = useState(0);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxMutatingId, setInboxMutatingId] = useState(null);
  const [inboxResponseDrafts, setInboxResponseDrafts] = useState({}); // id -> draft

  // Foundations issue #1 — company identity reconciliation. When the
  // dashboard loads, scan the coordinator's assessments for any whose
  // curated public.companies fields (name, industry) disagree with the
  // corresponding persona Q1/Q2 responses. The first one that needs
  // reconciliation surfaces a modal; on save, we re-check the remaining
  // assessments so a coordinator with multiple divergent assessments doesn't
  // have to refresh the page between each.
  const [pendingReconciliation, setPendingReconciliation] = useState(null);

  useEffect(() => {
    loadAssessments();
    loadInboxRequests();
  }, []);

  // After assessments load, look for the first one that needs the identity
  // reconciliation modal. Runs once per assessments-list change.
  useEffect(() => {
    if (loading || assessments.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const a of assessments) {
        const sessionId = a.session_id || a.id;
        if (!sessionId) continue;
        try {
          const state = await apiService.get(
            `/api/assessments/sessions/${sessionId}/identity-divergence`
          );
          if (cancelled) return;
          if (state && state.needs_modal) {
            setPendingReconciliation({ ...state, session_id: sessionId });
            return;
          }
        } catch (err) {
          // Non-fatal — dashboard still renders. Logged for debug.
          console.warn(`identity-divergence check failed for session ${sessionId}:`, err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loading, assessments]);

  const handleReconciliationResolved = () => {
    // Clear the modal, then re-run the scan so any remaining divergent
    // assessments queue up. setAssessments with same reference is a no-op
    // for the useEffect dep — bump by reloading from server which also picks
    // up any backend-side side-effects (e.g., snapshot rewrite).
    setPendingReconciliation(null);
    loadAssessments();
  };

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/coordinator/assessments');
      setAssessments(response.data || []);
    } catch (error) {
      console.error('Failed to load assessments:', error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInboxRequests = async () => {
    try {
      const response = await apiService.get('/api/coordinator/inbound-requests');
      if (response?.success) {
        setInboxRequests(response.requests || []);
        setInboxBadge(response.badge_count || 0);
      }
    } catch (error) {
      // Non-fatal — dashboard still renders
      console.warn('Failed to load inbox requests:', error);
    }
  };

  const updateInboundRequest = async (requestId, patch) => {
    setInboxMutatingId(requestId);
    try {
      await apiService.request(`/api/coordinator/inbound-requests/${requestId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch)
      });
      await loadInboxRequests();
    } catch (error) {
      console.error('Failed to update inbound request:', error);
    } finally {
      setInboxMutatingId(null);
    }
  };

  const calculateOverallProgress = (assessment) => {
    if (!assessment?.persona_sessions?.length) return 0;
    const totalProgress = assessment.persona_sessions.reduce((sum, ps) => {
      const questionsAnswered = ps.questions_answered || 0;
      const totalQuestions = ps.questions_total || 0;
      // Calculate percentage directly from database values
      const percentage = totalQuestions > 0 
        ? Math.min(100, (questionsAnswered / totalQuestions) * 100)
        : 0;
      return sum + percentage;
    }, 0);
    return Math.round(totalProgress / assessment.persona_sessions.length);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 50) return 'warning'; 
    if (percentage > 0) return 'info';
    return 'default';
  };

  const getStatusIcon = (percentage) => {
    if (percentage >= 100) return <CheckCircle aria-hidden="true" className="w-4 h-4" />;
    if (percentage > 0) return <Clock aria-hidden="true" className="w-4 h-4" />;
    return <AlertTriangle aria-hidden="true" className="w-4 h-4" />;
  };

  // Re-sends the magic-link invitation for one persona. This used to POST to
  // /api/coordinator/send-reminder, which logged a line, sent nothing, and
  // returned success — so the dashboard reported a delivery that never
  // happened. It now goes through the real SES path, and reports the
  // per-persona status the server actually returns rather than assuming.
  const sendEmailReminder = async (assessmentId, personaCode) => {
    try {
      setSendingEmail(true);
      setInviteNotice(null);

      const response = await apiService.sendAssessmentInvitations(assessmentId, true, personaCode);
      const result = response?.data?.results?.[0];

      if (result?.status === 'sent') {
        setInviteNotice({ tone: 'ok', text: `Invitation re-sent to ${result.recipient_masked}.` });
      } else if (result?.status === 'failed') {
        setInviteNotice({ tone: 'error', text: `Send failed: ${result.error || 'unknown error'}` });
      } else {
        // 'skipped', or no row at all — say so rather than claim a send.
        setInviteNotice({
          tone: 'error',
          text: result?.reason === 'already_accessed'
            ? 'Not re-sent: this persona has already opened their link.'
            : 'Nothing was sent — set up persona contacts for this assessment first.',
        });
      }

      await loadAssessments();
    } catch (error) {
      console.error('Failed to send email:', error);
      const detail = error?.response?.data?.error || error?.message || 'Please try again.';
      setInviteNotice({ tone: 'error', text: `Failed to send invitation. ${detail}` });
    } finally {
      setSendingEmail(false);
      setEmailModalOpen(false);
    }
  };

  const createNewAssessment = async () => {
    try {
      if (!newAssessmentData.company_name.trim()) {
        alert('Please enter a company name');
        return;
      }
      
      setLoading(true);
      const response = await apiService.post('/api/coordinator/assessments', {
        company_name: newAssessmentData.company_name.trim(),
        industry_vertical: newAssessmentData.industry_vertical.trim() || null
      });
      
      if (response.data && response.data.id) {
        // Navigate to the new assessment with company data pre-filled
        navigate(`/persona-assessment/${response.data.id}?company=${encodeURIComponent(newAssessmentData.company_name)}&industry=${encodeURIComponent(newAssessmentData.industry_vertical)}`);
      }
    } catch (error) {
      console.error('Failed to create assessment:', error);
      alert('Failed to create assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPersonaProgress = (assessment, personaCode) => {
    const personaSession = assessment.persona_sessions?.find(ps => ps.persona_code === personaCode);
    const questionsAnswered = personaSession?.questions_answered || 0;
    const totalQuestions = personaSession?.questions_total || 0;
    // Calculate percentage directly from database values
    return totalQuestions > 0 
      ? Math.min(100, Math.round((questionsAnswered / totalQuestions) * 100))
      : 0;
  };

  // PRISM linkage chip (Tile 6 — Apr 20 Foundations Strategy Scrub)
  const PrismLinkageChip = ({ linked, projectId }) => {
    if (linked) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/15 text-white ring-1 ring-white/25"
          title={`Linked to PRISM project #${projectId}`}
        >
          <Link2 aria-hidden="true" className="w-3 h-3" />
          Linked to PRISM
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 text-blue-100/70 ring-1 ring-white/10"
        title="No PRISM project linked to this assessment yet"
      >
        <Link2Off aria-hidden="true" className="w-3 h-3" />
        Not linked
      </span>
    );
  };

  // Progress Bar Component
  const OverallProgressBar = ({ assessment }) => {
    const personaProgresses = personas.map(persona => ({
      ...persona,
      progress: getPersonaProgress(assessment, persona.code)
    }));
    
    const overallProgress = calculateOverallProgress(assessment);
    
    return (
      <div data-tour="progress" className="ra-glass rounded-lg shadow-soft p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#e6f1ff]">Assessment Progress</h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-600">{overallProgress}%</div>
            <div className="text-sm text-[#a8b2d1]">Complete</div>
          </div>
        </div>
        
        {/* Overall Progress Bar */}
        <div className="w-full bg-[#1e3148] rounded-full h-3 mb-6">
          <div 
            className="bg-gradient-blue h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        
        {/* Individual Persona Progress */}
        <div className="grid grid-cols-4 gap-4">
          {personaProgresses.map((persona) => (
            <div key={persona.id} className="text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 ${
                persona.color === 'blue' ? 'gradient-blue' :
                persona.color === 'green' ? 'gradient-success' :
                persona.color === 'purple' ? 'gradient-purple' :
                'gradient-warning'
              } text-white`}>
                {persona.icon}
              </div>
              <div className="text-sm font-medium text-[#e6f1ff]">{persona.progress}%</div>
              <div className="text-xs text-[#a8b2d1]">{persona.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[rgba(255,255,255,0.12)] border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#a8b2d1] text-lg">Loading assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
      {/* Header */}
      <div className="ra-glass shadow-soft border-b border-[rgba(255,255,255,0.12)]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-[#e6f1ff] mb-2">
                Coordinator Dashboard
              </h1>
              <p className="text-lg text-[#a8b2d1]">
                Manage multi-persona assessments • {assessments.length} Active Assessment{assessments.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="flex space-x-4 items-center">
              <button
                data-tour="inbox"
                onClick={() => setInboxOpen(true)}
                className="relative inline-flex items-center px-4 py-2 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth shadow-soft hover-lift"
                aria-label={`Inbox${inboxBadge > 0 ? ` (${inboxBadge} open)` : ''}`}
              >
                <Inbox aria-hidden="true" className="w-4 h-4 mr-2" />
                Inbox
                {inboxBadge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-cyan-500 text-[#0a1929] text-xs font-bold flex items-center justify-center ring-2 ring-[#0a1929]">
                    {inboxBadge}
                  </span>
                )}
              </button>
              <button
                onClick={loadAssessments}
                className="inline-flex items-center px-4 py-2 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth shadow-soft hover-lift"
              >
                <RefreshCw aria-hidden="true" className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                data-tour="start-assessment"
                onClick={() => setShowNewAssessmentForm(true)}
                className="inline-flex items-center px-6 py-2 gradient-blue text-white font-semibold rounded-lg hover:shadow-lifted transition-smooth"
              >
                Start New Assessment
                <ChevronRight aria-hidden="true" className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invitation result. Carries the server's actual per-persona status —
          the old alert() claimed success unconditionally. */}
      {inviteNotice && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div
            role="status"
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
              inviteNotice.tone === 'ok'
                ? 'border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.08)] text-[#bbf7d0]'
                : 'border-[rgba(248,113,113,0.4)] bg-[rgba(248,113,113,0.08)] text-[#fecaca]'
            }`}
          >
            {inviteNotice.tone === 'ok'
              ? <CheckCircle aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0" />
              : <AlertTriangle aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0" />}
            <span className="flex-1">{inviteNotice.text}</span>
            <button
              onClick={() => setInviteNotice(null)}
              className="text-current opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* How It Works Guide */}
      <div data-tour="how-it-works" className="max-w-7xl mx-auto px-6 pt-4">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            onClick={() => window.dispatchEvent(new Event('tour:replay:coordinator-dashboard'))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: '1px solid rgba(96,165,250,0.4)',
              color: '#60a5fa',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: '0.8rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <HelpCircle aria-hidden="true" className="w-4 h-4" />
            Take a tour
          </button>
        </div>
        <HowItWorksGuide
          moduleName="Foundations"
          steps={[
            { title: 'Create a Session', desc: 'Start a new assessment for your organization. Each session captures a snapshot of your cyber risk posture from multiple perspectives.' },
            { title: 'Invite Personas', desc: 'Share assessment links with your CEO, CISO, CFO, HR, and Legal leads. Each persona answers the same 51 questions from their perspective — the multi-viewpoint approach reveals blind spots no single person can see.' },
            { title: 'Review Responses', desc: 'As personas complete their assessments, responses roll in. Track completion, see where perspectives diverge, and identify gaps between what leadership thinks and what practitioners know.' },
            { title: 'Analyze Results', desc: 'Your Cyber Risk Score (CRS) and Big 6 category scores are calculated automatically. The Assessment Dashboard shows where you\'re strong, where you\'re vulnerable, and how you compare to industry benchmarks.' },
            { title: 'Take Action', desc: 'Use Business Intelligence for executive insights — outside pressures, board reports, investment analysis. Use the Risk Optimization Studio for friction modeling. Export results or bridge to PRISM for technical deep-dive.' },
          ]}
          footnote="A typical assessment takes 15-20 minutes per persona. Results are available as soon as the first response is submitted and refine as more perspectives come in."
        />
      </div>

      {/* New Assessment Form Modal */}
      <Dialog.Root open={showNewAssessmentForm} onOpenChange={(open) => {
        if (!open) {
          setShowNewAssessmentForm(false);
          setNewAssessmentData({ company_name: '', industry_vertical: '' });
        }
      }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 ra-glass rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <Dialog.Title className="text-2xl font-semibold text-[#e6f1ff] mb-6">Start New Assessment</Dialog.Title>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={newAssessmentData.company_name}
                  onChange={(e) => setNewAssessmentData(prev => ({ ...prev, company_name: e.target.value }))}
                  aria-label="Company name"
                  className="w-full px-4 py-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter company name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                  Industry Vertical
                </label>
                <select
                  value={newAssessmentData.industry_vertical}
                  onChange={(e) => setNewAssessmentData(prev => ({ ...prev, industry_vertical: e.target.value }))}
                  aria-label="Industry vertical"
                  className="w-full px-4 py-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select industry (optional)</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Retail">Retail</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Energy">Energy</option>
                  <option value="Transportation and Logistics">Transportation and Logistics</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Hospitality and Tourism">Hospitality and Tourism</option>
                  <option value="Education">Education</option>
                  <option value="Defense">Defense</option>
                </select>
              </div>
            </div>
            
            <div className="flex space-x-4 mt-8">
              <Dialog.Close asChild>
                <button
                  className="flex-1 px-4 py-2 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                onClick={createNewAssessment}
                disabled={!newAssessmentData.company_name.trim()}
                className="flex-1 px-4 py-2 gradient-blue text-white font-medium rounded-lg hover:shadow-lifted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Assessment
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {assessments.length === 0 ? (
          <div className="text-center py-16">
            <Activity aria-hidden="true" className="w-16 h-16 mx-auto mb-4 text-[#5a6a8a]" />
            <h3 className="text-2xl font-semibold text-[#e6f1ff] mb-2">
              No assessments found
            </h3>
            <p className="text-[#a8b2d1] mb-8">
              Get started by creating your first multi-persona assessment
            </p>
            <button 
              onClick={() => setShowNewAssessmentForm(true)}
              className="inline-flex items-center px-6 py-3 gradient-blue text-white font-semibold rounded-lg hover:shadow-lifted transition-smooth"
            >
              Create First Assessment
              <ChevronRight aria-hidden="true" className="w-4 h-4 ml-2" />
            </button>
          </div>
        ) : (
          <div className="grid gap-8">
            {assessments.map((assessment, idx) => (
              <div key={assessment.id} data-tour={idx === 0 ? 'assessment-card' : undefined} className="ra-glass rounded-xl shadow-elevated hover:shadow-lifted transition-smooth overflow-hidden">
                {/* Progress Bar at Top */}
                <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
                  <OverallProgressBar assessment={assessment} />
                </div>

                {/* Assessment Header */}
                <div className="gradient-blue text-white p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-2xl font-semibold text-white">
                          {assessment.company_name || 'Assessment'}
                        </h3>
                        <PrismLinkageChip
                          linked={assessment.prism_linked}
                          projectId={assessment.prism_project_id}
                        />
                      </div>
                      <p className="text-blue-100">
                        {assessment.industry_vertical && `${assessment.industry_vertical} • `}
                        Created {new Date(assessment.created_at).toLocaleDateString()} • ID: {assessment.id}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold mb-1 text-white">
                        {calculateOverallProgress(assessment)}%
                      </div>
                      <div className="text-blue-100 text-sm">
                        Overall Progress
                      </div>
                    </div>
                  </div>
                </div>

                {/* TODO Section for Email Setup */}
                {(() => {
                  const overallProgress = calculateOverallProgress(assessment);
                  const pendingPersonas = personas.filter(persona => {
                    const progress = getPersonaProgress(assessment, persona.code);
                    return progress < 10; // Show TODO if persona has less than 10% progress
                  });
                  
                  if (pendingPersonas.length > 0 && overallProgress < 50) {
                    return (
                      <div className="mx-6 mb-6 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <AlertTriangle aria-hidden="true" className="h-5 w-5 text-orange-400" />
                          </div>
                          <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-[#ccd6f6]">
                              📋 TODO: Set Up Email Assignments
                            </h3>
                            <div className="mt-2 text-sm text-orange-700">
                              <p className="mb-2">
                                <strong>Action Required:</strong> Assign email addresses to personas who haven't started their assessments yet.
                              </p>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                {pendingPersonas.map(persona => (
                                  <li key={persona.id}>
                                    <strong>{persona.name}</strong> - {persona.description}
                                  </li>
                                ))}
                              </ul>
                              <p className="mt-3 font-medium">
                                💡 Once you assign emails, use the <Mail aria-hidden="true" className="h-4 w-4 inline mx-1" /> button to send assessment invitations.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Persona Progress Grid */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {personas.map((persona) => {
                      const personaSession = assessment.persona_sessions?.find(ps => ps.persona_code === persona.code);
                      const questionsAnswered = personaSession?.questions_answered || 0;
                      const totalQuestions = personaSession?.questions_total || 0;
                      // Calculate percentage directly from database values
                      const progress = totalQuestions > 0 
                        ? Math.min(100, Math.round((questionsAnswered / totalQuestions) * 100))
                        : 0;
                      
                      return (
                        <div key={persona.id} className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 hover:border-primary-300 transition-smooth">
                          <div className="flex items-center mb-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                              persona.color === 'blue' ? 'gradient-blue' :
                              persona.color === 'green' ? 'gradient-success' :
                              persona.color === 'purple' ? 'gradient-purple' :
                              'gradient-warning'
                            } text-white`}>
                              {persona.icon}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#e6f1ff] text-sm">
                                {persona.name}
                              </h4>
                              <p className="text-xs text-[#a8b2d1]">
                                {questionsAnswered} of {totalQuestions} questions
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${
                                progress >= 100 ? 'text-green-600' : progress > 0 ? 'text-orange-500' : 'text-[#5a6a8a]'
                              }`}>
                                {progress}%
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="w-full bg-[#1e3148] rounded-full h-2 mb-4">
                            <div 
                              className={`h-2 rounded-full transition-smooth ${
                                progress >= 100 ? 'gradient-success' : progress > 0 ? 'gradient-warning' : 'bg-[rgba(255,255,255,0.12)]'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/persona-assessment/${assessment.id}?persona=${persona.code}`)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-[#ccd6f6] ra-glass border border-[rgba(255,255,255,0.18)] rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-smooth"
                            >
                              {progress > 0 ? 'Continue' : 'Start'}
                              <ChevronRight aria-hidden="true" className="w-3 h-3 ml-1" />
                            </button>
                            {progress < 100 && (
                              <button
                                onClick={() => sendEmailReminder(assessment.id, persona.code)}
                                disabled={sendingEmail}
                                className="inline-flex items-center px-3 py-2 text-xs font-medium text-[#ccd6f6] ra-glass border border-[rgba(255,255,255,0.18)] rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-smooth"
                              >
                                <Mail aria-hidden="true" className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Assessment Actions */}
                  <div className="mt-6 pt-6 border-t border-[rgba(255,255,255,0.12)]">
                    <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                      {calculateOverallProgress(assessment) >= 100 ? (
                        // Completed assessment buttons
                        <>
                          <button
                            onClick={() => navigate(`/assessment/${assessment.id}/results`)}
                            className="inline-flex items-center px-6 py-3 gradient-blue text-white font-medium rounded-lg hover:shadow-lifted transition-smooth"
                          >
                            <BarChart3 aria-hidden="true" className="w-4 h-4 mr-2" />
                            View Results
                          </button>
                          <button
                            onClick={() => navigate(`/persona-assessment/${assessment.id}?skipPolicy=true`)}
                            className="inline-flex items-center px-4 py-2 bg-[#172a45] text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[#1e3148] transition-smooth"
                          >
                            <Settings aria-hidden="true" className="w-4 h-4 mr-2" />
                            Review/Edit
                          </button>
                        </>
                      ) : (
                        // Incomplete assessment buttons
                        <>
                          <button
                            onClick={() => {
                              setInviteNotice(null);
                              setPersonaSetupFor(assessment);
                            }}
                            className="inline-flex items-center px-6 py-3 gradient-blue text-white font-medium rounded-lg hover:shadow-lifted transition-smooth"
                          >
                            <Send aria-hidden="true" className="w-4 h-4 mr-2" />
                            Invite Participants
                          </button>
                          <button
                            onClick={() => navigate(`/persona-assessment/${assessment.id}`)}
                            className="inline-flex items-center px-6 py-3 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth"
                          >
                            <Eye aria-hidden="true" className="w-4 h-4 mr-2" />
                            Continue Assessment
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Persona contacts + invitations. Until this was wired there was no
          route to PersonaSetup at all, so a coordinator had no way to send a
          real invitation from the product. */}
      <Dialog.Root
        open={!!personaSetupFor}
        onOpenChange={(open) => { if (!open) setPersonaSetupFor(null); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,900px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-0 text-[#e6f1ff] max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.12)]">
              <div>
                <Dialog.Title className="text-lg font-semibold">Invite participants</Dialog.Title>
                <Dialog.Description className="text-sm text-[#a8b2d1]">
                  Each participant gets a secure link to their own portion of the assessment.
                </Dialog.Description>
              </div>
              <Dialog.Close className="text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" aria-label="Close">
                <X className="w-5 h-5" />
              </Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {personaSetupFor && (
                <PersonaSetup
                  sessionId={personaSetupFor.id}
                  companyName={personaSetupFor.company_name || 'this assessment'}
                  onCancel={() => setPersonaSetupFor(null)}
                  onComplete={(result) => {
                    setPersonaSetupFor(null);
                    const sent = (result?.results?.results || []).filter((r) => r.status === 'sent');
                    const failed = (result?.results?.results || []).filter((r) => r.status === 'failed');
                    setInviteNotice(
                      failed.length > 0
                        ? { tone: 'error', text: `${sent.length} invitation(s) sent, ${failed.length} failed. Check the participant addresses and try again.` }
                        : { tone: 'ok', text: `${sent.length} invitation(s) sent.` }
                    );
                    loadAssessments();
                  }}
                />
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Tile 15 — Inbound PM requests */}
      <Dialog.Root open={inboxOpen} onOpenChange={setInboxOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,820px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-0 text-[#e6f1ff] max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.12)]">
              <div>
                <Dialog.Title className="text-lg font-semibold">Portfolio Manager requests</Dialog.Title>
                <Dialog.Description className="text-sm text-[#a8b2d1]">
                  Requests from MSSPs, insurance partners, or other Portfolio Managers who monitor your organization.
                </Dialog.Description>
              </div>
              <Dialog.Close className="text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" aria-label="Close">
                <X className="w-5 h-5" />
              </Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {inboxRequests.length === 0 && (
                <div className="py-12 text-center text-sm text-[#a8b2d1]">
                  No inbound requests. When a Portfolio Manager asks for an assessment update or clarification, it will appear here.
                </div>
              )}
              {inboxRequests.map((r) => {
                const isMutating = inboxMutatingId === r.id;
                const draft = inboxResponseDrafts[r.id] ?? '';
                const priorityColor = r.priority === 'urgent'
                  ? 'bg-red-500/20 text-red-200 border-red-400/40'
                  : r.priority === 'high'
                  ? 'bg-orange-500/20 text-orange-200 border-orange-400/40'
                  : r.priority === 'low'
                  ? 'bg-slate-500/20 text-slate-200 border-slate-400/40'
                  : 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40';
                const statusColor = r.status === 'pending'
                  ? 'bg-cyan-500/20 text-cyan-200'
                  : r.status === 'acknowledged'
                  ? 'bg-amber-500/20 text-amber-200'
                  : r.status === 'in_progress'
                  ? 'bg-blue-500/20 text-blue-200'
                  : r.status === 'completed'
                  ? 'bg-emerald-500/20 text-emerald-200'
                  : 'bg-slate-500/20 text-slate-200';
                const typeLabels = {
                  update_assessment: 'Update assessment',
                  complete_assessment: 'Complete assessment',
                  clarify_response: 'Clarify response',
                  schedule_review: 'Schedule review'
                };

                return (
                  <div key={r.id} className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-sm font-medium text-[#e6f1ff]">
                          {typeLabels[r.request_type] || r.request_type}
                        </div>
                        <div className="text-xs text-[#a8b2d1]">
                          {r.pm_organization || 'Portfolio Manager'}
                          {r.pm_email ? ` • ${r.pm_email}` : ''}
                          {' • '}
                          {new Date(r.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${priorityColor}`}>
                          {r.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusColor}`}>
                          {r.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-[#ccd6f6] whitespace-pre-wrap mb-3">{r.request_message}</p>

                    {r.due_date && (
                      <div className="text-xs text-[#a8b2d1] mb-2">
                        Requested by: <span className="text-amber-300">{new Date(r.due_date).toLocaleDateString()}</span>
                      </div>
                    )}

                    {r.client_response && (
                      <div className="text-xs bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded p-3 mb-2">
                        <div className="text-[#a8b2d1] mb-1">Your response:</div>
                        <div className="text-[#ccd6f6] whitespace-pre-wrap">{r.client_response}</div>
                      </div>
                    )}

                    {r.status !== 'completed' && r.status !== 'declined' && (
                      <>
                        <textarea
                          rows={2}
                          value={draft}
                          onChange={(e) => setInboxResponseDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                          placeholder="Optional response (shown to the requester)"
                          className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-2 py-1.5 text-xs text-[#e6f1ff] placeholder:text-[#64748b] mb-2"
                        />
                        <div className="flex flex-wrap gap-2 justify-end">
                          {r.status === 'pending' && (
                            <button
                              disabled={isMutating}
                              onClick={() => updateInboundRequest(r.id, { status: 'acknowledged', ...(draft ? { client_response: draft } : {}) })}
                              className="text-xs px-3 py-1.5 rounded-md border border-amber-400/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
                            >
                              Acknowledge
                            </button>
                          )}
                          {r.status !== 'in_progress' && (
                            <button
                              disabled={isMutating}
                              onClick={() => updateInboundRequest(r.id, { status: 'in_progress', ...(draft ? { client_response: draft } : {}) })}
                              className="text-xs px-3 py-1.5 rounded-md border border-blue-400/40 bg-blue-500/15 text-blue-200 hover:bg-blue-500/25 disabled:opacity-50"
                            >
                              Mark in progress
                            </button>
                          )}
                          <button
                            disabled={isMutating}
                            onClick={() => updateInboundRequest(r.id, { status: 'completed', ...(draft ? { client_response: draft } : {}) })}
                            className="text-xs px-3 py-1.5 rounded-md border border-emerald-400/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50"
                          >
                            Mark complete
                          </button>
                          <button
                            disabled={isMutating}
                            onClick={() => updateInboundRequest(r.id, { status: 'declined', ...(draft ? { client_response: draft } : {}) })}
                            className="text-xs px-3 py-1.5 rounded-md border border-red-400/40 bg-red-500/15 text-red-200 hover:bg-red-500/25 disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-3 border-t border-[rgba(255,255,255,0.12)] flex justify-between items-center text-xs text-[#a8b2d1]">
              <span>{inboxRequests.length} total • {inboxBadge} open</span>
              <button
                onClick={loadInboxRequests}
                className="inline-flex items-center gap-1 text-[#ccd6f6] hover:text-[#e6f1ff] transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <CompanyIdentityReconciliationModal
        isOpen={!!pendingReconciliation}
        data={pendingReconciliation}
        onClose={() => setPendingReconciliation(null)}
        onResolved={handleReconciliationResolved}
      />

      <GuidedTour tourId="coordinator-dashboard" steps={coordinatorTourSteps} />
    </div>
  );
};

export default CoordinatorDashboard;